import React, { useState, useEffect } from 'react';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, FormGroup, FieldError } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import { getRoles, getPhones, getMainPhoneList, updateUser, RoleInfoResponse, PhoneInfoResponse, MainPhoneResponse, UserInfoResponse } from '../../api/user';
import { listTeams, TeamResponse } from '../../api/team';
import { formatPhoneNumber } from '../../utils/format';
import toast from 'react-hot-toast';
import { getErrorMessage, getResponseMessage } from '../../utils/apiMessage';

interface AccountEditModalProps {
  onClose: () => void;
  onSuccess: () => void;
  user: UserInfoResponse | null;
}


const AccountEditModal: React.FC<AccountEditModalProps> = ({ onClose, onSuccess, user }) => {
  const [roles, setRoles] = useState<RoleInfoResponse[]>([]);
  const [phones, setPhones] = useState<PhoneInfoResponse[]>([]);
  const [mainPhones, setMainPhones] = useState<MainPhoneResponse[]>([]);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    password: '',
    username: user?.username || '',
    roleId: '',
    teamId: user?.teamId?.toString() || '',
    phone: user?.phone || '',
    dodTargetId: user?.dodTargetId?.toString() || ''
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    setIsInitialLoading(true);
    setHasLoadError(false);
    const fetchData = async () => {
      try {
        const [rolesData, phonesData, mainPhonesData, teamsData] = await Promise.all([
          getRoles(),
          getPhones(),
          getMainPhoneList(),
          listTeams({ limit: 100, pg: 1 })
        ]);
        if (!isCurrent) return;
        setRoles(rolesData);
        setPhones(phonesData);
        setMainPhones(mainPhonesData.content);
        setTeams(teamsData.content);

        // roleId가 없을 경우 roleName으로 찾아서 설정
        if (user?.roleName) {
          const matchedRole = rolesData.find(r => r.name === user.roleName);
          if (matchedRole) {
            setFormData(prev => (
              prev.roleId ? prev : { ...prev, roleId: matchedRole.id.toString() }
            ));
          }
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error('Failed to fetch modal data:', error);
        setHasLoadError(true);
      } finally {
        if (isCurrent) setIsInitialLoading(false);
      }
    };
    fetchData();
    return () => { isCurrent = false; };
  }, [user, retryKey]);

  if (!user) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateRequiredFields = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.username.trim()) nextErrors.username = '사용자명을 입력해주세요.';

    setFieldErrors(nextErrors);

    const firstErrorField = Object.keys(nextErrors)[0];
    if (firstErrorField) {
      window.setTimeout(() => {
        const element = document.querySelector<HTMLElement>(`[name="${firstErrorField}"]`);
        element?.focus();
      }, 0);
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isInitialLoading || hasLoadError) return;
    if (!validateRequiredFields()) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateUser(user.userId, {
        username: formData.username,
        password: formData.password || undefined,
        phone: formData.phone,
        ...(formData.roleId ? { roleId: Number(formData.roleId) } : {}),
        ...(formData.teamId ? { teamId: Number(formData.teamId) } : {}),
        ...(formData.dodTargetId ? { dodTargetId: Number(formData.dodTargetId) } : {}),
        kind: user.kind
      });
      toast.success(getResponseMessage(response, '계정 정보가 수정되었습니다.'));
      onSuccess();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(getErrorMessage(error, '사용자 정보 수정에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title="계정 정보 수정" onClose={onClose} historyMode="none" width={520} footer={<>
      <Button $variant="secondary" onClick={onClose}>취소</Button>
      <Button onClick={handleSubmit} disabled={isLoading || isInitialLoading || hasLoadError}>{isLoading ? '수정 중...' : '수정하기'}</Button>
    </>}>
      {isInitialLoading && <PageState status="loading" title="선택 항목을 불러오는 중입니다." />}
      {hasLoadError && <PageState status="error" title="선택 항목을 불러오지 못했습니다." onRetry={() => setRetryKey(key => key + 1)} />}
        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-loginId">로그인 아이디</ConsoleLabel>
          <ConsoleInput id="AccountEditModal-loginId" defaultValue={user.loginId} readOnly style={{ opacity: 0.7, cursor: 'not-allowed' }} />
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-password">비밀번호</ConsoleLabel>
          <ConsoleInput id="AccountEditModal-password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="비밀번호 변경 시에만 입력하세요"
          />
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-username">사용자명</ConsoleLabel>
          <ConsoleInput id="AccountEditModal-username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="이름을 입력하세요"
            aria-invalid={Boolean(fieldErrors.username)}
            aria-describedby={fieldErrors.username ? 'account-edit-username-error' : undefined}
          />
          {fieldErrors.username && <FieldError id="account-edit-username-error">{fieldErrors.username}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-teamId">소속 팀 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountEditModal-teamId"
            name="teamId"
            value={formData.teamId}
            onChange={handleChange}
            aria-label="소속 팀"
          >
            <option value="">소속 팀을 선택하세요</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.displayName}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-roleId">역할 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountEditModal-roleId"
            name="roleId"
            value={formData.roleId}
            onChange={handleChange}
          >
            <option value="">역할을 선택하세요</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-phone">내선번호 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountEditModal-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          >
            <option value="">내선번호를 선택하세요</option>
            {phones.map(p => (
              <option key={p.id} value={p.phone}>{p.phone}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountEditModal-dodTargetId">발신번호 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountEditModal-dodTargetId"
            name="dodTargetId"
            value={formData.dodTargetId}
            onChange={handleChange}
          >
            <option value="">발신번호를 선택하세요</option>
            {mainPhones.map(p => (
              <option key={p.id} value={p.id}>{formatPhoneNumber(p.phone)}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

    </Modal>
  );
};

export default AccountEditModal;
